/**
 * Kiosk launch-config state classification.
 *
 * Every kiosk load resolves a `practiceId` and an `ehrLocationId`. This module
 * turns the raw resolution inputs into ONE named bucket so a fleet of tablets
 * can be counted and triaged from a single Datadog facet during rollout.
 *
 * Pure and side-effect free — the emitter (`log-kiosk-config-state.ts`) owns the
 * Datadog call. Keeping the classification separate makes every bucket
 * unit-testable without mocking the logger.
 *
 * The buckets map 1:1 to distinct operational owners:
 *   healthy_url / healthy_cache          → nobody (but cache-only wants a URL fix)
 *   degraded_cached_location             → investigate: running on an unverified location
 *   blocked_practice_not_found           → fix the practiceId in the launch URL
 *   blocked_practice_has_no_location     → fix the practice record in config-manager
 *   blocked_location_fetch_failed        → infra; self-heals on the next load
 *   blocked_no_practice_id               → fix the launch URL (missing/typo'd param)
 *   stuck_loading                        → infra; practice fetch never settled
 */

/** Where the resolved `practiceId` came from. */
export type PracticeIdSource = "url" | "cache" | "none";

/** Where the resolved `ehrLocationId` came from. */
export type EhrLocationIdSource = "derived" | "cache" | "none";

export type KioskConfigState =
  | "healthy_url"
  | "healthy_cache"
  | "degraded_cached_location"
  | "blocked_practice_not_found"
  | "blocked_practice_has_no_location"
  | "blocked_location_fetch_failed"
  | "blocked_no_practice_id"
  | "stuck_loading";

/** Coarse rollup so a dashboard can show healthy/degraded/blocked without listing every bucket. */
export type KioskConfigOutcome = "healthy" | "degraded" | "blocked";

export interface ClassifyKioskConfigInput {
  practiceIdSource: PracticeIdSource;
  ehrLocationIdSource: EhrLocationIdSource;
  /**
   * Whether the resolved practiceId was found in the org's practice list.
   * `undefined` when the list never loaded (so we can't tell) — that case is
   * reported as a fetch failure instead of a missing practice.
   */
  practiceFoundInOrg: boolean | undefined;
  /** The practice-list query errored or produced no data. */
  practiceFetchFailed: boolean;
}

export function classifyKioskConfigState(
  input: ClassifyKioskConfigInput,
): KioskConfigState {
  const {
    practiceIdSource,
    ehrLocationIdSource,
    practiceFoundInOrg,
    practiceFetchFailed,
  } = input;

  // No practice at all — the launch URL supplied nothing and the cache is empty.
  // This is the typo'd-param / wiped-storage population.
  if (practiceIdSource === "none") {
    return "blocked_no_practice_id";
  }

  // A location was resolved. Which source decides healthy vs degraded: only a
  // value DERIVED from the resolved practice is provably that practice's.
  if (ehrLocationIdSource === "derived") {
    return practiceIdSource === "url" ? "healthy_url" : "healthy_cache";
  }
  if (ehrLocationIdSource === "cache") {
    return "degraded_cached_location";
  }

  // No location. Separate the three causes — they have different owners.
  if (practiceFetchFailed) {
    return "blocked_location_fetch_failed";
  }
  if (practiceFoundInOrg === false) {
    return "blocked_practice_not_found";
  }
  return "blocked_practice_has_no_location";
}

const OUTCOME_BY_STATE: Record<KioskConfigState, KioskConfigOutcome> = {
  healthy_url: "healthy",
  healthy_cache: "healthy",
  degraded_cached_location: "degraded",
  blocked_practice_not_found: "blocked",
  blocked_practice_has_no_location: "blocked",
  blocked_location_fetch_failed: "blocked",
  blocked_no_practice_id: "blocked",
  stuck_loading: "blocked",
};

export function outcomeForKioskConfigState(
  state: KioskConfigState,
): KioskConfigOutcome {
  return OUTCOME_BY_STATE[state];
}
